import { inc as semverIncrement } from "semver-ts";
import {
  MutableRefObject,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import {
  ConnectionHandler,
  graphql,
  useFragment,
  useMutation,
} from "react-relay";
import { generatePath, useNavigate } from "react-router";
import { logger } from "@/common/logger";
import { InferOutput, object, optional, string } from "valibot";
import { Loading01Icon } from "@/icons";
import { Button, ButtonProps } from "@/kit/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/kit/ui/select";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalPortal,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredStar,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";
import { useValibotForm } from "@/utils/validation";
import { toast } from "@/utils/toast";
import { relayErrorMessage } from "@/utils/relay";
import { semverSchema } from "@/utils/schema";
import { WorkspacePublishButtonFragment$key } from "./__generated__/WorkspacePublishButtonFragment.graphql";
import { WorkspacePublishButtonMutation } from "./__generated__/WorkspacePublishButtonMutation.graphql";
import { splitWorkspaceSlug } from "@/utils/routing";

const Fragment = graphql`
  fragment WorkspacePublishButtonFragment on Workspace {
    id
    viewerCanCreateVersion: viewerCan(action: CREATE_WORKSPACE_VERSION)
    baseVersions: versions(first: 50) {
      nodes {
        id
        version
        publishedAt
      }
    }
  }
`;

const Mutation = graphql`
  mutation WorkspacePublishButtonMutation(
    $workspaceId: ID!
    $input: CreateWorkspaceVersionInput!
    $connections: [ID!]!
  ) {
    createWorkspaceVersion(id: $workspaceId, input: $input)
      @prependNode(
        connections: $connections
        edgeTypeName: "WorkspaceVersionEdge"
      ) {
      version
      workspace {
        slug
      }
      ...WorkspaceVersionHistoryItemFragment
    }
  }
`;

export interface WorkspacePublishButtonProps extends ButtonProps {
  workspace: WorkspacePublishButtonFragment$key;
}

export default function WorkspacePublishButton({
  workspace,
  children,
  ...props
}: WorkspacePublishButtonProps) {
  const [isOpen, onOpen] = useState(false);

  return (
    <WorkspacePublishModal
      open={isOpen}
      onOpenChange={onOpen}
      workspace={workspace}
    >
      <ResponsiveModalTrigger asChild>
        <Button {...props}>
          {children ?? <FormattedMessage defaultMessage="New version" />}
        </Button>
      </ResponsiveModalTrigger>
    </WorkspacePublishModal>
  );
}

export interface WorkspacePublishModalProps extends PropsWithChildren {
  workspace: WorkspacePublishButtonFragment$key;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkspacePublishModal({
  open,
  onOpenChange: onOpenChangeProp,
  children,
  workspace: workspaceFragment,
}: WorkspacePublishModalProps) {
  const workspace = useFragment(Fragment, workspaceFragment);
  const isMutationInFlight = useRef(false);

  if (!workspace.viewerCanCreateVersion) {
    return null;
  }

  const baseVersion = workspace.baseVersions.nodes[0]?.version;
  const nextVersion = baseVersion
    ? semverIncrement(baseVersion, "major")
    : null;

  const onOpenChange = (openChange: boolean) => {
    if (isMutationInFlight.current) return;
    onOpenChangeProp?.(openChange);
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      {children}

      <ResponsiveModalPortal>
        <ResponsiveModalContent
          className="p-0 gap-0 overflow-hidden flex flex-col w-[80vw] lg:w-150 max-h-[80%] lg:max-h-[67%]"
          noClose
        >
          <ResponsiveModalHeader className="flex-none px-4 py-3 border-b border-grey-200 lg:border-0 lg:p-6">
            <ResponsiveModalTitle className="text-start text-xl">
              <FormattedMessage defaultMessage="Create new version" />
            </ResponsiveModalTitle>
          </ResponsiveModalHeader>

          <PublishForm
            workspaceId={workspace.id}
            initialVersion={nextVersion}
            baseVersions={workspace.baseVersions.nodes}
            isMutationInFlight={isMutationInFlight}
            onClose={() => onOpenChangeProp(false)}
          />
        </ResponsiveModalContent>
      </ResponsiveModalPortal>
    </ResponsiveModal>
  );
}

const FormSchema = (intl: IntlShape) =>
  object({
    version: semverSchema(intl),
    fromVersionId: optional(string()),
  });
type SubmitData = InferOutput<ReturnType<typeof FormSchema>>;

interface BaseVersion {
  readonly id: string;
  readonly version: string;
  readonly publishedAt?: string | null | undefined;
}

function PublishForm(props: {
  workspaceId: ID;
  initialVersion?: string | null | undefined;
  baseVersions: ReadonlyArray<BaseVersion>;
  isMutationInFlight: MutableRefObject<boolean>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const formSchema = useMemo(() => FormSchema(intl), [intl]);
  const form = useValibotForm(formSchema, {
    defaultValues: {
      version: props.initialVersion ?? undefined,
      fromVersionId: props.baseVersions[0]?.id,
    },
  });
  const [commitMutation, isMutationInFlight] =
    useMutation<WorkspacePublishButtonMutation>(Mutation);
  props.isMutationInFlight.current = isMutationInFlight;

  const onSubmit = (submitData: SubmitData) =>
    commitMutation({
      variables: {
        workspaceId: props.workspaceId,
        input: {
          version: submitData.version,
          fromVersionId: submitData.fromVersionId || null,
        },
        connections: [
          ConnectionHandler.getConnectionID(
            props.workspaceId,
            "WorkspaceVersionHistoryPaginationFragment_versions",
          ),
          ConnectionHandler.getConnectionID(
            props.workspaceId,
            "WorkspaceAsideFragment_versions",
          ),
          ConnectionHandler.getConnectionID(
            props.workspaceId,
            "WorkspaceScaffoldWorkspaceFragment_latest",
          ),
        ],
      },
      onCompleted: ({ createWorkspaceVersion: version }) => {
        props.onClose();
        navigate(
          generatePath("/:owner/:slug/versions/:version", {
            ...splitWorkspaceSlug(version.workspace.slug),
            version: version.version,
          }),
        );
        toast.success(
          <FormattedMessage
            defaultMessage="Draft version {version} created"
            values={{ version: version.version }}
          />,
        );
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });

  return (
    <Form form={form} onSubmit={onSubmit}>
      <div className="flex-1 flex flex-col gap-6 px-4 py-3 overflow-y-auto lg:px-6 lg:py-0 lg:pb-6">
        <FormField
          control={form.control}
          name="version"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage defaultMessage="Version" />
                <RequiredStar />
              </FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter a unique SemVer name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {props.baseVersions.length > 0 && (
          <FormField
            control={form.control}
            name="fromVersionId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage defaultMessage="Based on" />
                </FormLabel>
                <FormControl>
                  <Select
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {props.baseVersions.map((base) => (
                        <SelectItem key={base.id} value={base.id}>
                          <FormattedMessage
                            defaultMessage="v{version} {state}"
                            values={{
                              version: base.version,
                              state: base.publishedAt ? "(Active)" : "(Draft)",
                            }}
                          />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <p className="text-sm font-normal text-grey-600">
                  <FormattedMessage defaultMessage="The new version starts as a copy of this version's files." />
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      <ResponsiveModalFooter className="flex-none flex-row p-4 border-t border-grey-60 lg:border-none">
        <ResponsiveModalClose asChild>
          <Button
            variant="secondary"
            className="flex-1 lg:flex-initial"
            disabled={isMutationInFlight}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
        </ResponsiveModalClose>

        <Button
          type="submit"
          disabled={isMutationInFlight}
          className="flex-1 lg:flex-initial"
        >
          {isMutationInFlight && (
            <Loading01Icon className="w-5 h-5 animate-spin" />
          )}
          <FormattedMessage defaultMessage="Create version" />
        </Button>
      </ResponsiveModalFooter>
    </Form>
  );
}
